import { useCallback, useEffect, useState } from 'react'

import { Stack, Tooltip } from '@mui/material'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'

import { CardTitle } from 'components/common/controls'
import { DefaultDropCharge } from 'constants/EntityConstants/Booking'
import {
  BookingChargeType,
  useDeleteBookingChargeMutation,
  useDeleteWaitAndReturnChargeMutation,
  useGetBookingTariffLazyQuery,
  useGetWaitAndReturnFeeCategoriesQuery,
} from 'generated/graphql'
import { decimalFormatter } from 'helpers'
import { Restricted, emitter } from 'providers'
import { TBookingChargeForm, TBookingForm } from 'types/form-types'

import ChargeMoreInfoDialog from './ChargeMoreInfoDialog'
import { ChargeTable } from './ChargeTable'
import { ChargeToolbar } from './ChargeToolbar'
import { TChargeToolbarForm } from './ChargeToolbar/chargeToolbar.types'

interface IProps {
  hoverText?: string
  disabled: boolean
}

const OtherCharges = (props: IProps) => {
  const { hoverText = '', disabled } = props
  const [openChargeDescriptionDialog, setOpenChargeDescriptionDialog] = useState(false)
  const [selectedFieldIndex, setSelectedFieldIndex] = useState<number | null>(null)

  const { getValues, setValue, control } = useFormContext<TBookingForm>()
  const { fields, append, update, remove } = useFieldArray({
    name: 'charges',
    keyName: 'chargeKey',
  })

  const [isWaitAndReturn, consignmentFee, waitAndReturnFeeCategoryId] = useWatch({
    control,
    name: ['isWaitAndReturn', 'consignmentFee', 'waitAndReturnFeeCategoryId'],
  })

  const [getBookingTariffDetails] = useGetBookingTariffLazyQuery()
  const { data: waitAndReturnFeeCategoriesData } = useGetWaitAndReturnFeeCategoriesQuery()

  const [deleteBookingCharge] = useDeleteBookingChargeMutation()
  const [deleteWaitAndReturnCharge] = useDeleteWaitAndReturnChargeMutation()

  const handleAddDropCharge = useCallback(
    async (overrideCharges?: TBookingChargeForm[], overrideDropQuantity?: number) => {
      const [collectionAddressesValue, deliveryAddressesValue, chargesValue, isWaitAndReturnValue] =
        getValues(['collectionAddresses', 'deliveryAddresses', 'charges', 'isWaitAndReturn'])

      const charges = overrideCharges ?? chargesValue

      // Do not add Drop charge if a Wait and Return charge exists
      const hasWaitAndReturnCharge = charges?.some(
        (x) => x?.type === BookingChargeType.WaitAndReturn,
      )
      if (hasWaitAndReturnCharge) {
        return
      }

      // Exclude the W&R delivery address from the drop quantity calculation
      const adjustedDeliveryCount = isWaitAndReturnValue
        ? deliveryAddressesValue.length - 1
        : deliveryAddressesValue.length

      const dropQuantity =
        overrideDropQuantity ?? collectionAddressesValue.length + adjustedDeliveryCount - 2

      const currentDropCharge = charges?.find((x) => x?.type === BookingChargeType.Drop)

      // Remove existing Drop charge if present
      if (dropQuantity <= 0) {
        if (currentDropCharge) {
          const dropChargeIndex = charges.indexOf(currentDropCharge)
          if (dropChargeIndex !== -1) {
            if (currentDropCharge.id) {
              deleteBookingCharge({
                variables: { bookingChargeId: Number(currentDropCharge.id) },
              }).catch((err) => {
                console.error('Failed to delete Drop charge', err)
              })
            }
            remove(dropChargeIndex)
          }
        }
        return
      }

      // More than 2 Addresses
      if (dropQuantity >= 1) {
        const tariffIdValue = getValues('tariffId')
        if (!tariffIdValue) {
          return
        }

        const tariffDetailsResult = await getBookingTariffDetails({
          variables: { id: Number(tariffIdValue) },
        })

        const tariffDetails = tariffDetailsResult.data?.tariffById
        if (!tariffDetails || !tariffDetails.isDropChargeEnabled) {
          return
        }

        const rate = tariffDetails.isUseDefaultDropCharge
          ? DefaultDropCharge
          : tariffDetails.dropChargeRate ?? 0

        // Add new Drop Charge
        if (currentDropCharge === undefined) {
          const newDropCharge: TBookingChargeForm = {
            name: 'Drop',
            type: BookingChargeType.Drop,
            quantity: dropQuantity,
            rate,
            isDeleted: false,
            isAutomatic: false,
          }

          append(newDropCharge, { shouldFocus: false })
          return
        }

        // Update current Drop Charge quantity and rate
        const updatedDropCharge = {
          ...currentDropCharge,
          quantity: dropQuantity,
          rate,
        }

        const dropChargeIndex = charges.indexOf(currentDropCharge)
        dropChargeIndex !== -1 && update(dropChargeIndex, updatedDropCharge)
      }
    },
    [append, deleteBookingCharge, getBookingTariffDetails, getValues, remove, update],
  )

  useEffect(() => {
    emitter.on('add-drop-charge', () => handleAddDropCharge())

    return () => emitter.off('add-drop-charge')
  }, [handleAddDropCharge])

  const handleAddCharge = (formData: TChargeToolbarForm) => {
    const { name, type, rate, vatCategoryId, xeroServiceCode, description } = formData
    const chargesValue = getValues().charges
    let existingChargeIndex

    const existingCharge = chargesValue?.find((charge, index) => {
      existingChargeIndex = index
      return charge.name.trim() === name?.trim()
    })

    if (existingCharge) {
      setValue(`charges.${existingChargeIndex}.quantity`, Number(existingCharge.quantity) + 1, {
        shouldDirty: true,
      })
      return
    }

    append({
      name,
      type,
      quantity: 1,
      rate: rate ? rate : 1,
      vatCategoryId,
      xeroServiceCode,
      description,
    })
  }

  const handleOnRemoveClick = (index: number, id: string) => {
    if (id) {
      deleteBookingCharge({ variables: { bookingChargeId: Number(id) } }).then((result) => {
        if (!result.data?.deleteBookingCharge) {
          toast.error('Something went wrong')
        }

        toast.success('Charge was deleted with success')
        remove(index)
      })
    } else {
      remove(index)
    }
  }

  const handleOnInfoClick = (index: number) => {
    setSelectedFieldIndex(index)
    setOpenChargeDescriptionDialog(true)
  }

  const handleCloseChargeDescriptionDialog = () => {
    setOpenChargeDescriptionDialog(false)
  }

  const handleSaveChargeDescriptionDialog = ({
    vatCategoryId,
    xeroServiceCode,
    description,
  }: {
    vatCategoryId: string
    xeroServiceCode: string
    description: string
  }) => {
    setOpenChargeDescriptionDialog(false)

    if (selectedFieldIndex !== null) {
      setValue(`charges.${selectedFieldIndex}.vatCategoryId`, vatCategoryId, {
        shouldDirty: true,
      })
      setValue(`charges.${selectedFieldIndex}.xeroServiceCode`, xeroServiceCode, {
        shouldDirty: true,
      })
      setValue(`charges.${selectedFieldIndex}.description`, description, { shouldDirty: true })
    }
  }

  const updateWaitAndReturnCharge = useCallback(() => {
    const {
      isWaitAndReturn,
      consignmentFee,
      waitAndReturnFeeCategoryId,
      charges: chargesValue = [],
    } = getValues()

    const charges = chargesValue as TBookingChargeForm[]

    const waitAndReturnChargeIndex = charges.findIndex(
      (charge) => charge?.type === BookingChargeType.WaitAndReturn,
    )
    const waitAndReturnCharge =
      waitAndReturnChargeIndex >= 0 ? charges[waitAndReturnChargeIndex] : undefined

    if (!isWaitAndReturn) {
      if (!waitAndReturnCharge) {
        return
      }

      if (waitAndReturnCharge.id) {
        deleteWaitAndReturnCharge({
          variables: { bookingChargeId: Number(waitAndReturnCharge.id) },
        }).catch((err) => {
          console.error('Failed to delete Wait & Return charge', err)
        })
      }

      remove(waitAndReturnChargeIndex)

      const [collectionAddresses, deliveryAddresses] = getValues([
        'collectionAddresses',
        'deliveryAddresses',
      ])

      // When the W&R charge contains an ID, it has already been saved → implies an extra delivery address → adjust by -1
      // When the W&R charge has no ID, it hasn't been saved → no extra delivery address → no adjustment needed
      const adjustedDeliveryCount = waitAndReturnCharge.id
        ? deliveryAddresses.length - 1
        : deliveryAddresses.length

      const dropQuantity = collectionAddresses.length + adjustedDeliveryCount - 2

      const chargesWithoutWaitAndReturn = charges.filter(
        (charge) => charge?.type !== BookingChargeType.WaitAndReturn,
      )

      handleAddDropCharge(chargesWithoutWaitAndReturn, dropQuantity)

      return
    }

    // Remove Drop charge if WaitAndReturn is being added
    const dropChargeIndex = charges.findIndex((charge) => charge?.type === BookingChargeType.Drop)
    if (dropChargeIndex !== -1) {
      const dropCharge = charges[dropChargeIndex]
      if (dropCharge.id) {
        deleteBookingCharge({ variables: { bookingChargeId: Number(dropCharge.id) } }).catch(
          (err) => {
            console.error('Failed to delete Drop charge', err)
          },
        )
      }
      remove(dropChargeIndex)
    }

    const feeCategories = waitAndReturnFeeCategoriesData?.waitAndReturnFeeCategories ?? []
    const selectedCategory = feeCategories.find(
      (category) => String(category.id) === String(waitAndReturnFeeCategoryId),
    )
    const waitAndReturnPercent = selectedCategory?.percent ?? 0

    const rawWaitAndReturnFee =
      consignmentFee && waitAndReturnPercent
        ? (Number(consignmentFee) * waitAndReturnPercent) / 100
        : 0

    const rate = Number(decimalFormatter.format(rawWaitAndReturnFee).replace(/,/g, ''))

    if (!waitAndReturnCharge) {
      const newCharge: TBookingChargeForm = {
        name: 'Wait and Return Fee',
        type: BookingChargeType.WaitAndReturn,
        quantity: 1,
        rate,
        isDeleted: false,
        isAutomatic: false,
      }

      append(newCharge, { shouldFocus: false })

      return
    }

    if (waitAndReturnCharge.rate !== rate) {
      update(waitAndReturnChargeIndex, {
        ...waitAndReturnCharge,
        rate,
        quantity: 1,
      })
    }
  }, [
    append,
    deleteBookingCharge,
    deleteWaitAndReturnCharge,
    getValues,
    handleAddDropCharge,
    remove,
    update,
    waitAndReturnFeeCategoriesData,
  ])

  useEffect(() => {
    if (!waitAndReturnFeeCategoriesData) return

    updateWaitAndReturnCharge()
  }, [
    waitAndReturnFeeCategoriesData,
    isWaitAndReturn,
    consignmentFee,
    waitAndReturnFeeCategoryId,
    updateWaitAndReturnCharge,
  ])

  return (
    <Tooltip title={disabled ? hoverText : ''} followCursor>
      <div>
        <CardTitle disabled={disabled}>Other Charges</CardTitle>
        <Stack spacing={2}>
          <Restricted to='AddBookingCharge'>
            <ChargeToolbar disabled={disabled} onAddCharge={handleAddCharge} />
          </Restricted>

          <ChargeTable
            fields={fields}
            disabled={disabled}
            onRemoveClick={handleOnRemoveClick}
            onInfoClick={handleOnInfoClick}
          />
        </Stack>

        {openChargeDescriptionDialog && selectedFieldIndex !== null && (
          <ChargeMoreInfoDialog
            selectedFieldIndex={selectedFieldIndex}
            openChargeDescriptionDialog={openChargeDescriptionDialog}
            onCloseDialog={handleCloseChargeDescriptionDialog}
            onSaveClick={handleSaveChargeDescriptionDialog}
          />
        )}
      </div>
    </Tooltip>
  )
}

export default OtherCharges
